import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Upload,
  Eye,
  Plus,
  X,
  Trash2,
  GripVertical,
  Users,
  Calendar,
  School,
  Shield,
  Star,
  Award,
  Heart,
  TrendingUp,
  Target,
  Building2,
  Quote,
  Link as LinkIcon,
  Image,
  BarChart3,
  MessageSquare,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'users', label: 'Users', icon: Users },
  { value: 'calendar', label: 'Calendar', icon: Calendar },
  { value: 'school', label: 'School', icon: School },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'trending', label: 'Trending', icon: TrendingUp },
  { value: 'target', label: 'Target', icon: Target },
  { value: 'building', label: 'Building', icon: Building2 },
];

const SocialProofEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const logoInputRef = useRef(null);
  const headshotInputRefs = useRef({});

  const [config, setConfig] = useState({
    section_heading: '',
    section_subheading: '',
    metrics: [],
    testimonials: [],
    logos_heading: '',
    partner_logos: [],
    google_review_url: '',
    google_review_button_text: '',
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => {
      const newConfig = typeof updater === 'function' ? updater(prev) : updater;
      return newConfig;
    });
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (confirmed) navigate(-1);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/social-proof-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/social-proof-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });

      if (res.ok) {
        toast.success('Social proof saved successfully');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'social_logo');

      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        const newLogo = {
          id: `logo-${Date.now()}`,
          url: data.image_url,
          alt_text: file.name.split('.')[0],
          order: config.partner_logos.length,
        };
        updateConfig(prev => ({
          ...prev,
          partner_logos: [...prev.partner_logos, newLogo],
        }));
        toast.success('Logo uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleHeadshotUpload = async (file, testimonialId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'social_headshot');

      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        updateConfig(prev => ({
          ...prev,
          testimonials: prev.testimonials.map(t =>
            t.id === testimonialId ? { ...t, headshot_url: data.image_url } : t
          ),
        }));
        toast.success('Headshot uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload headshot');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Metrics handlers
  const updateMetric = (index, field, value) => {
    updateConfig(prev => ({
      ...prev,
      metrics: prev.metrics.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));
  };

  // Testimonial handlers
  const addTestimonial = () => {
    if (config.testimonials.length >= 12) {
      toast.error('Maximum 12 testimonials allowed');
      return;
    }
    const newTestimonial = {
      id: `testimonial-${Date.now()}`,
      quote: '',
      name: '',
      role: 'Parent',
      school: '',
      location: '',
      headshot_url: null,
      verified: true,
    };
    updateConfig(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, newTestimonial],
    }));
  };

  const updateTestimonial = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      testimonials: prev.testimonials.map(t => (t.id === id ? { ...t, [field]: value } : t)),
    }));
  };

  const removeTestimonial = (id) => {
    updateConfig(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter(t => t.id !== id),
    }));
  };

  // Logo handlers
  const removeLogo = (id) => {
    updateConfig(prev => ({
      ...prev,
      partner_logos: prev.partner_logos.filter(l => l.id !== id),
    }));
  };

  const updateLogoAlt = (id, alt_text) => {
    updateConfig(prev => ({
      ...prev,
      partner_logos: prev.partner_logos.map(l => (l.id === id ? { ...l, alt_text } : l)),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="social-proof-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Social Proof Editor</h1>
            <p className="text-slate-500">Edit trust metrics, testimonials, and partner logos</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/#social-proof', '_blank')} className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-social-proof-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Section Heading */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Section Heading
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={config.section_heading}
              onChange={(e) => updateConfig(prev => ({ ...prev, section_heading: e.target.value }))}
              placeholder="Trusted by Families & Schools"
            />
          </div>
          <div>
            <Label>Subheading</Label>
            <Input
              value={config.section_subheading}
              onChange={(e) => updateConfig(prev => ({ ...prev, section_subheading: e.target.value }))}
              placeholder="Join thousands of swimmers..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Trust Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Trust Metrics (4 cards)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.metrics.map((metric, idx) => (
              <div key={idx} className="p-4 border rounded-lg space-y-3 bg-slate-50">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  Metric {idx + 1}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Value</Label>
                    <Input
                      value={metric.value}
                      onChange={(e) => updateMetric(idx, 'value', e.target.value)}
                      placeholder="1000+"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={metric.label}
                      onChange={(e) => updateMetric(idx, 'label', e.target.value)}
                      placeholder="Swimmers Trained"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Select value={metric.icon} onValueChange={(v) => updateMetric(idx, 'icon', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        return (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Testimonials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Testimonials ({config.testimonials.length}/12)
            </div>
            <Button variant="outline" size="sm" onClick={addTestimonial} disabled={config.testimonials.length >= 12}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.testimonials.map((t) => (
            <div key={t.id} className="p-4 border rounded-lg space-y-3 bg-slate-50 relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeTestimonial(t.id)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Headshot */}
                <div>
                  <Label className="text-xs">Headshot</Label>
                  <input
                    ref={(el) => (headshotInputRefs.current[t.id] = el)}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleHeadshotUpload(e.target.files[0], t.id)}
                  />
                  <div
                    onClick={() => headshotInputRefs.current[t.id]?.click()}
                    className="w-16 h-16 rounded-full border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer overflow-hidden bg-white flex items-center justify-center"
                  >
                    {t.headshot_url ? (
                      <img src={getImageUrl(t.headshot_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Upload className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Name & Role */}
                <div className="md:col-span-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={t.name}
                      onChange={(e) => updateTestimonial(t.id, 'name', e.target.value)}
                      placeholder="Sarah M."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={t.role} onValueChange={(v) => updateTestimonial(t.id, 'role', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Parent">Parent</SelectItem>
                        <SelectItem value="School Admin">School Admin</SelectItem>
                        <SelectItem value="Student">Student</SelectItem>
                        <SelectItem value="Partner">Partner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs">Quote</Label>
                <Textarea
                  value={t.quote}
                  onChange={(e) => updateTestimonial(t.id, 'quote', e.target.value)}
                  placeholder="My daughter went from being terrified of water..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">School (optional)</Label>
                  <Input
                    value={t.school || ''}
                    onChange={(e) => updateTestimonial(t.id, 'school', e.target.value)}
                    placeholder="School name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Location (optional)</Label>
                  <Input
                    value={t.location || ''}
                    onChange={(e) => updateTestimonial(t.id, 'location', e.target.value)}
                    placeholder="Mbabane"
                  />
                </div>
              </div>
            </div>
          ))}

          {config.testimonials.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No testimonials yet. Click "Add" to create one.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Partner Logos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              Partner Logos
            </div>
            <div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
              />
              <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1" />
                Upload Logo
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Section Label</Label>
            <Input
              value={config.logos_heading}
              onChange={(e) => updateConfig(prev => ({ ...prev, logos_heading: e.target.value }))}
              placeholder="Trusted by Schools & Stakeholders"
            />
          </div>

          {config.partner_logos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {config.partner_logos.map((logo) => (
                <div key={logo.id} className="relative group p-3 border rounded-lg bg-white">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLogo(logo.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                  <img
                    src={getImageUrl(logo.url)}
                    alt={logo.alt_text || 'Logo'}
                    className="h-12 w-full object-contain mb-2"
                  />
                  <Input
                    value={logo.alt_text || ''}
                    onChange={(e) => updateLogoAlt(logo.id, e.target.value)}
                    placeholder="Alt text"
                    className="text-xs h-8"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
              <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No logos uploaded yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Reviews */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-primary" />
            Google Reviews Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Google Business Profile URL</Label>
            <Input
              value={config.google_review_url}
              onChange={(e) => updateConfig(prev => ({ ...prev, google_review_url: e.target.value }))}
              placeholder="https://g.page/r/..."
            />
          </div>
          <div>
            <Label>Button Text</Label>
            <Input
              value={config.google_review_button_text}
              onChange={(e) => updateConfig(prev => ({ ...prev, google_review_button_text: e.target.value }))}
              placeholder="Read Reviews on Google"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default SocialProofEditor;
